import {
  LanguageChannelPair,
  LanguageSelectorConfig,
} from '@/features/projects/projectsSlice';
import {
  MAX_LANGUAGE_LABEL_LENGTH,
  MAX_LANGUAGE_PAIRS,
} from '@/features/channels/channelsSlice';

// Pulled out of LanguageSelectorSettings.tsx on purpose: this is plain logic
// with no React/HeroUI imports, so it can be unit-tested (and reused by
// Project.tsx's save gate) without dragging the component tree — and
// everything it transitively imports — along for the ride.

export interface LanguageSelectorRowIssues {
  label?:				string;
  lang?:				string;
  list?:				string;
}

export interface LanguageSelectorValidation {
  valid:				boolean;
  globalError:			string;
  rows:					LanguageSelectorRowIssues[];
}

// Client-side mirror of the server's ValidateLanguageSelector (§4.3). A
// list_id that no longer resolves is intentionally NOT checked here — the
// lists arrive asynchronously, so treating "not found" as blocking would
// flag every row while they load. That case is a warning, rendered by
// LanguageSelectorSettings from `lists`, not by this function.
export function validateLanguageSelector(config: LanguageSelectorConfig | null | undefined): LanguageSelectorValidation {
  const pairs = config?.pairs || [];

  const langCounts: {[lang: string]: number} = {};
  pairs.forEach((pair: LanguageChannelPair) => {
    if (!pair.lang)
      return;

    langCounts[pair.lang] = (langCounts[pair.lang] || 0) + 1;
  });

  let valid = true;

  const rows: LanguageSelectorRowIssues[] = pairs.map((pair: LanguageChannelPair) => {
    const issues: LanguageSelectorRowIssues = {};

    if (!pair.label || pair.label.trim() == '') {
      issues.label = 'err_empty_label';
    } else if (pair.label.length > MAX_LANGUAGE_LABEL_LENGTH) {
      issues.label = 'err_long_label';
    }

    if (!pair.lang || pair.lang.trim() == '') {
      issues.lang = 'err_empty_lang';
    } else if (langCounts[pair.lang] > 1) {
      issues.lang = 'err_dup_lang';
    }

    if (!pair.list_id || pair.list_id.trim() == '') {
      issues.list = 'err_empty_list';
    }

    if (issues.label || issues.lang || issues.list)
      valid = false;

    return issues;
  });

  let globalError = '';

  if (pairs.length > MAX_LANGUAGE_PAIRS) {
    globalError = 'err_too_many';
  } else if (config?.enabled && pairs.length == 0) {
    globalError = 'err_enabled_no_pairs';
  }

  if (globalError)
    valid = false;

  return { valid, globalError, rows };
}

export function getNewLanguageChannelPair(order: number): LanguageChannelPair {
  return {
    key:				"",
    label:				"",
    lang:				"",
    list_id:			"",
    order:				order,
  };
}
